import { IEventBus, TEventBusListener } from './IEventBus';

type TCallbacksSet<T> = T;

export class EventBus<TEvents> implements IEventBus<TEvents> {
  public appId: string | null = null;

  private _lastUid: number = -1;
  private _messages: Partial<Record<keyof TEvents, TCallbacksSet<TEvents>>> = {};

  private readonly _immediateExceptions: boolean = false;
  private readonly _allSubscribingMsg: string = '*';

  constructor(immediateExceptions: boolean = true) {
    this._immediateExceptions = immediateExceptions;
  }

  public setAppId(id: string): void {
    this.appId = id;
  }

  public getAppId(): string | null {
    return this.appId;
  }

  public publish<E extends keyof TEvents>(event: E, data: TEvents[E]): boolean {
    return this._publish(event, data, false, this._immediateExceptions);
  }

  public publishSync<E extends keyof TEvents>(event: E, data: TEvents[E]): boolean {
    return this._publish(event, data, true, this._immediateExceptions);
  }

  public subscribe<E extends keyof TEvents>(
    event: E,
    callback: TEventBusListener<TEvents[E]>,
  ): string {
    if (typeof callback !== 'function') {
      return '';
    }

    const token: string = 'uid_' + String(++this._lastUid);

    if (!this._messages[event]) {
      this._messages[event] = {} as TEvents;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const d: TCallbacksSet<E> = this._messages[event] || {};

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    d[token] = callback;

    // return token for unsubscribing
    return token;
  }

  public toggleSubscriptionBulk(data: {
    action: 'subscribe' | 'unsubscribe';
    callbacks: {
      [event in keyof TEvents]?: TEventBusListener<TEvents[event]>;
    };
  }) {
    if (data.action === 'subscribe') {
      Object.entries(data.callbacks).forEach(([key, cb]) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.subscribe.call(this, key, cb);
      });
    } else {
      // eslint-disable-next-line no-unused-vars
      Object.entries(data.callbacks).forEach(([_key, cb]) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.unsubscribe.call(this, cb);
      });
    }
  }

  public subscribeAll(callback: TEventBusListener<TEvents>): boolean | string {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.subscribe(this._allSubscribingMsg as keyof TEvents, callback);
  }

  public subscribeOnce<E extends keyof TEvents>(
    event: E,
    callback: TEventBusListener<TEvents[E]>,
  ): EventBus<TEvents> {
    const token: string = this.subscribe(event, (...args) => {
      // before func apply, unsubscribe message
      this.unsubscribe(token);
      callback.apply(this, args);
    });

    return this;
  }

  public clearAllSubscriptions(): void {
    this._messages = {};
  }

  public clearSubscriptions(topic: string): void {
    for (const m in this._messages) {
      if (Object.prototype.hasOwnProperty.call(this._messages, m) && m.indexOf(topic) === 0) {
        delete this._messages[m as keyof TEvents];
      }
    }
  }

  public unsubscribe<E extends keyof TEvents>(
    value: string | TEventBusListener<E>,
  ): boolean | string | void {
    const isTopic: boolean =
      typeof value === 'string' &&
      (Object.prototype.hasOwnProperty.call(this._messages, value) ||
        this._descendantTopicExists(value as E));
    const isToken: boolean = !isTopic && typeof value === 'string';
    const isFunction: boolean = typeof value === 'function';
    let result: boolean | string = false;

    if (isTopic) {
      this.clearSubscriptions(value as string);
      return;
    }

    for (const m in this._messages) {
      if (Object.prototype.hasOwnProperty.call(this._messages, m)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const message: TCallbacksSet<E> = this._messages[m as E] || {};

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (isToken && message[value as string]) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          delete message[value as string];
          result = value as string;
          // tokens are unique, so we can just stop here
          break;
        }

        if (isFunction) {
          for (const t in message) {
            if (Object.prototype.hasOwnProperty.call(message, t) && message[t] === value) {
              delete message[t];
              result = true;
            }
          }
        }
      }
    }

    return result;
  }

  private _hasKeys<E extends keyof TEvents>(obj: TCallbacksSet<E>): boolean {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        return true;
      }
    }
    return false;
  }

  private _throwException(e: Error): () => void {
    return () => {
      throw e;
    };
  }

  private _callSubscriberWithDelayedExceptions<E extends keyof TEvents>(
    subscriber: TEventBusListener<TEvents[E]>,
    data: TEvents[E],
  ): void {
    try {
      subscriber(data);
    } catch (ex) {
      setTimeout(this._throwException(ex as Error), 0);
    }
  }

  private _callSubscriberWithImmediateExceptions<E extends keyof TEvents>(
    subscriber: TEventBusListener<TEvents[E]>,
    data: TEvents[E],
  ): void {
    subscriber(data);
  }

  private _deliverMessage<E extends keyof TEvents>(
    originalMessage: E,
    matchedMessage: E,
    data: TEvents[E],
    immediateExceptions: boolean,
  ): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const subscribers: TCallbacksSet<E> = this._messages[matchedMessage] || {};
    const callSubscriber: (subscriber: TEventBusListener<TEvents[E]>, data: TEvents[E]) => void =
      immediateExceptions
        ? this._callSubscriberWithImmediateExceptions
        : this._callSubscriberWithDelayedExceptions;

    if (!Object.prototype.hasOwnProperty.call(this._messages, matchedMessage)) {
      return;
    }

    for (const s in subscribers) {
      if (Object.prototype.hasOwnProperty.call(subscribers, s)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        callSubscriber(subscribers[s], data);
      }
    }
  }

  private _createDeliveryFunction<E extends keyof TEvents>(
    event: E,
    data: TEvents[E],
    immediateExceptions: boolean,
  ): () => void {
    return () => {
      let topic: string = String(event);
      let position: number = topic.lastIndexOf('.');

      this._deliverMessage(event, event, data, immediateExceptions);

      while (position > -1) {
        topic = topic.substr(0, position);
        position = topic.lastIndexOf('.');
        this._deliverMessage(event, topic as E, data, immediateExceptions);
      }

      this._deliverMessage(event, this._allSubscribingMsg as E, data, immediateExceptions);
    };
  }

  private _hasDirectSubscribersFor<E extends keyof TEvents>(message: E): boolean {
    const topic: E = String(message) as E;
    return Boolean(
      Object.prototype.hasOwnProperty.call(this._messages, topic) &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this._hasKeys(this._messages[topic] || {}),
    );
  }

  private _messageHasSubscribers<E extends keyof TEvents>(message: E): boolean {
    let topic: string = String(message);
    let found: boolean =
      this._hasDirectSubscribersFor(topic as E) ||
      this._hasDirectSubscribersFor(this._allSubscribingMsg as E);
    let position: number = topic.lastIndexOf('.');

    while (!found && position !== -1) {
      topic = topic.substr(0, position);
      position = topic.lastIndexOf('.');
      found = this._hasDirectSubscribersFor(topic as E);
    }

    return found;
  }

  private _publish<E extends keyof TEvents>(
    message: E,
    data: TEvents[E],
    sync: boolean,
    immediateExceptions: boolean,
  ): boolean {
    const normalizedMessage: E =
      typeof message === 'symbol' ? ((message as symbol).toString() as E) : message;

    const deliver: () => void = this._createDeliveryFunction(
      normalizedMessage,
      data,
      immediateExceptions,
    );
    const hasSubscribers: boolean = this._messageHasSubscribers(normalizedMessage);

    if (!hasSubscribers) {
      return false;
    }

    if (sync) {
      deliver();
    } else {
      setTimeout(deliver, 0);
    }
    return true;
  }

  private _descendantTopicExists<E extends keyof TEvents>(topic: E): boolean {
    for (const m in this._messages) {
      if (
        Object.prototype.hasOwnProperty.call(this._messages, m) &&
        m.indexOf(topic as string) === 0
      ) {
        return true;
      }
    }

    return false;
  }
}

const eventBusInstance: EventBus<any> = new EventBus();
export default eventBusInstance;
